<script setup lang="ts">
import { ref } from 'vue'
import DropZone from './components/DropZone.vue'
import EngineChoice from './components/EngineChoice.vue'
import FileList from './components/FileList.vue'
import OutputPanel from './components/OutputPanel.vue'
import { useConversions } from './composables/useConversions'
import type { EnginePreference } from './core/types'

const engine = ref<EnginePreference>('auto')

const {
  jobs,
  selected,
  selectedId,
  markdown,
  html,
  htmlSource,
  busy,
  doneCount,
  add,
  select,
  remove,
  clear,
} = useConversions(engine)

function onFiles(files: File[]): void {
  void add(files)
}
</script>

<template>
  <div class="flex h-full flex-col">
    <header
      class="border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900"
    >
      <div class="mx-auto flex max-w-7xl items-center gap-3">
        <h1 class="text-base font-semibold tracking-tight">document-parser</h1>
        <p class="hidden text-sm text-slate-500 sm:block dark:text-slate-400">
          Documents to clean Markdown &amp; HTML, entirely in your browser
        </p>
        <span
          v-if="busy"
          class="ml-auto animate-pulse text-xs font-medium text-sky-600 dark:text-sky-400"
        >
          Converting…
        </span>
      </div>
    </header>

    <main
      class="mx-auto grid w-full max-w-7xl min-h-0 flex-1 grid-cols-1 gap-4 p-4 lg:grid-cols-[20rem_1fr]"
    >
      <div class="flex min-h-0 flex-col gap-3">
        <DropZone @files="onFiles" />
        <EngineChoice v-model="engine" />

        <div v-if="jobs.length" class="flex min-h-0 flex-1 flex-col">
          <div class="mb-1 flex items-center justify-between px-1">
            <h2 class="text-xs font-semibold tracking-wide text-slate-500 uppercase">
              Files ({{ doneCount }}/{{ jobs.length }})
            </h2>
            <button
              type="button"
              class="text-xs text-slate-500 underline-offset-2 hover:text-rose-600 hover:underline"
              @click="clear"
            >
              Clear all
            </button>
          </div>
          <div class="min-h-0 flex-1 overflow-y-auto">
            <FileList :jobs="jobs" :selected-id="selectedId" @select="select" @remove="remove" />
          </div>
        </div>
      </div>

      <OutputPanel :job="selected" :markdown="markdown" :html="html" :html-source="htmlSource" />
    </main>
  </div>
</template>
